import { useCallback, type Dispatch, type SetStateAction } from 'react'
import { toast } from 'sonner'
import api from '@/services/api'
import type { BdrLead } from '@/types'
import { getFriendlyError } from '@/utils/getFriendlyError'

export function useBdrPanelPendencias(
  selectedLead: BdrLead | null,
  setSelectedLead: Dispatch<SetStateAction<BdrLead | null>>,
) {
  const handleAddPending = useCallback(
    async (texto: string, dataRetorno?: string) => {
      if (!selectedLead) return
      try {
        const { data } = await api.post(`/bdr/leads/${selectedLead.id}/pendencias`, {
          texto,
          dataRetorno: dataRetorno || null,
        })
        setSelectedLead((prev) => {
          if (!prev) return prev
          return {
            ...prev,
            pendencias: [...(prev.pendencias ?? []), data.data],
          }
        })
        toast.success('Pendência adicionada')
      } catch (err: unknown) {
        toast.error(getFriendlyError(err, 'Erro ao adicionar pendência'))
      }
    },
    [selectedLead, setSelectedLead],
  )

  const togglePending = useCallback(
    async (pendingId: number) => {
      if (!selectedLead) return
      try {
        await api.patch(`/bdr/leads/${selectedLead.id}/pendencias/${pendingId}`)
        setSelectedLead((prev) => {
          if (!prev) return prev
          return {
            ...prev,
            pendencias: (prev.pendencias ?? []).map((p) =>
              p.id === pendingId
                ? {
                    ...p,
                    resolved: !p.resolved,
                    resolvedAt: p.resolved ? null : new Date().toISOString(),
                  }
                : p,
            ),
          }
        })
      } catch (err: unknown) {
        toast.error(getFriendlyError(err, 'Erro ao atualizar pendência'))
      }
    },
    [selectedLead, setSelectedLead],
  )

  const removePending = useCallback(
    async (pendingId: number) => {
      if (!selectedLead) return
      try {
        await api.delete(`/bdr/leads/${selectedLead.id}/pendencias/${pendingId}`)
        setSelectedLead((prev) => {
          if (!prev) return prev
          return {
            ...prev,
            pendencias: (prev.pendencias ?? []).filter((p) => p.id !== pendingId),
          }
        })
        toast.success('Pendência removida')
      } catch (err: unknown) {
        toast.error(getFriendlyError(err, 'Erro ao remover pendência'))
      }
    },
    [selectedLead, setSelectedLead],
  )

  return { handleAddPending, togglePending, removePending }
}
